import { inject, Injectable } from '@angular/core';
import { EnvironmentToken } from '@jump-tech-frontend/app-config';
import { RelayEnvironment } from './domain/relay-environment';
import { datadogLogs } from '@datadog/browser-logs';
import { v4 as uuidv4 } from 'uuid';
import { HttpGateway } from './http-gateway.service';

/**
 * Tracking event types for relay user interactions
 */
export type RelayTrackingEventType = 'relay_tracking_session_started' | 'relay_tracking_card_transition';

/**
 * Device type detected from user agent
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'unknown';

/**
 * Source of relay access (from URL parameters)
 */
export type RelaySource = 'email' | 'sms' | 'unknown';

/**
 * Information about the user's device
 */
export interface DeviceInfo {
  type: DeviceType;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Information about a specific relay card
 */
export interface CardInfo {
  cardKey: string;
  label: string;
  index: number;
  id?: string;
  conditionallyVisible?: boolean;
  skipped?: boolean;
}

/**
 * Completion metrics for relay progress
 */
export interface CompletionMetrics {
  currentStep: number;
  totalSteps: number;
  completionPercentage: number;
}

/**
 * Complete tracking event with all context
 */
export interface RelayTrackingEvent {
  eventType: RelayTrackingEventType;
  timestamp: string;
  sessionId: string;
  suid: string | null;
  projectId: string;
  source: RelaySource;
  device: DeviceInfo;
  completion: CompletionMetrics;
  fromCard?: CardInfo;
  toCard?: CardInfo;
}

/**
 * Request body for the POST /relay/tracking endpoint
 */
export interface RelayTrackingRequestBody {
  tenant: string | null;
  projectId: string;
  eventType: RelayTrackingEventType;
  timestamp: string;
  sessionId: string;
  suid: string | null;
  device: DeviceInfo;
  source: string;
  fromCard?: CardInfo;
  toCard?: CardInfo;
}

/**
 * Tracking data embedded in the relay form submission POST body
 */
export interface SubmissionTrackingData {
  timestamp: string;
  sessionId: string;
  source: RelaySource;
  device: DeviceInfo;
}

/**
 * Service for tracking relay user interactions and card transitions.
 * Posts tracking events to the tracking API.
 */
@Injectable({
  providedIn: 'root'
})
export class RelayTrackingService {
  private readonly sessionId: string;
  private readonly source: RelaySource = 'unknown';
  private readonly device: DeviceInfo;
  private suid: string | null = null;
  private projectId = '';
  private tenant: string | null = null;
  private currentCard: CardInfo | null = null;
  private previousCard: CardInfo | null = null;
  private currentStep = 0;
  private totalSteps = 0;

  private httpGateway = inject(HttpGateway);
  private environment = inject<RelayEnvironment>(EnvironmentToken);

  constructor() {
    this.sessionId = uuidv4();
    this.device = this.detectDeviceInfo();
    this.source = this.detectSource();
  }

  /**
   * Initialize tracking with relay-specific context
   */
  public initialize(suid: string | null, totalSteps: number, projectId: string, tenant: string): void {
    this.suid = suid;
    this.totalSteps = totalSteps;
    this.projectId = projectId;
    this.tenant = tenant;
  }

  /**
   * Update the current card being viewed
   */
  public setCurrentCard(
    cardKey: string,
    label: string,
    index: number,
    step: number,
    conditionallyVisible: boolean,
    skipped?: boolean
  ): void {
    this.previousCard = this.currentCard;
    this.currentCard = {
      cardKey,
      label,
      index,
      conditionallyVisible,
      skipped
    };
    this.currentStep = step;
  }

  /**
   * Build tracking data to embed in the relay form submission POST body
   */
  public getSubmissionTrackingData(): SubmissionTrackingData {
    return {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      source: this.source,
      device: this.device
    };
  }

  /**
   * Track a relay event
   */
  public trackEvent(eventType: RelayTrackingEventType): void {
    // Calculate completion percentage - currently not posted to tracking API
    const completionPercentage =
      this.totalSteps > 0 && this.currentStep > 0 ? Math.round((this.currentStep / this.totalSteps) * 100) : 0;

    const event: RelayTrackingEvent = {
      eventType,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      suid: this.suid,
      projectId: this.projectId,
      source: this.source,
      device: this.device,
      completion: {
        currentStep: this.currentStep,
        totalSteps: this.totalSteps,
        completionPercentage
      }
    };

    // Add card transition information if applicable
    if (eventType === 'relay_tracking_card_transition') {
      if (this.previousCard) {
        event.fromCard = this.previousCard;
      }
      if (this.currentCard) {
        event.toCard = this.currentCard;
      }
    }

    if (eventType === 'relay_tracking_session_started' || eventType === 'relay_tracking_card_transition') {
      this.postTrackingEvent(event);
    }
  }

  /**
   * POST tracking event to the API (fire-and-forget)
   */
  private postTrackingEvent(event: RelayTrackingEvent): void {
    if (!this.environment.apiRelayTrackingUrl) {
      return;
    }

    const body: RelayTrackingRequestBody = {
      tenant: this.tenant,
      projectId: this.projectId,
      eventType: event.eventType,
      timestamp: event.timestamp,
      sessionId: event.sessionId,
      suid: event.suid,
      device: event.device,
      source: event.source,
      fromCard: event.fromCard,
      toCard: event.toCard
    };

    this.httpGateway
      .post(this.environment.apiRelayTrackingUrl, body, {
        headers: { Authorization: `suid ${this.suid}` }
      })
      .then()
      .catch(err => {
        datadogLogs.logger.error('Relay Tracking', { errorContext: { body } }, err);
      });
  }

  /**
   * Detect device type from user agent and screen size
   */
  private detectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent || '';
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    let deviceType: DeviceType = 'unknown';

    // Mobile detection
    if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = 'mobile';
    }
    // Tablet detection
    else if (/iPad|Android/i.test(userAgent) && screenWidth >= 768) {
      deviceType = 'tablet';
    }
    // Desktop
    else if (screenWidth >= 1024) {
      deviceType = 'desktop';
    }

    return {
      type: deviceType,
      userAgent,
      screenWidth,
      screenHeight
    };
  }

  /**
   * Detect relay source from URL parameters
   */
  private detectSource(): RelaySource {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sourceParam = urlParams.get('source');

      if (sourceParam === 'email') {
        return 'email';
      } else if (sourceParam === 'sms') {
        return 'sms';
      }

      // Try to infer from other URL parameters if source not explicitly set
      if (urlParams.has('utm_medium')) {
        const utmMedium = urlParams.get('utm_medium')?.toLowerCase();
        if (utmMedium === 'email') {
          return 'email';
        } else if (utmMedium === 'sms') {
          return 'sms';
        }
      }

      return 'unknown';
    } catch (error) {
      console.error('[Relay Tracking] Error detecting source:', error);
      return 'unknown';
    }
  }
}
